/**
 * Plugin Initializer
 *
 * Registers plugins once on app startup (client-side)
 */

'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { registerAllPlugins } from '@/lib/platform/plugins';
import { logger } from '@/lib/platform/utils/logger';

export function PluginInitializer({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [pluginsRegistered, setPluginsRegistered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if this is a public page that doesn't need plugins
  const isPublicPage =
    pathname?.startsWith('/proposal/') ||
    pathname?.startsWith('/auth/') ||
    pathname === '/trust-center' ||
    pathname?.startsWith('/trust-center/') ||
    pathname === '/';

  useEffect(() => {
    // Skip plugin registration for public pages (the render guard below
    // already treats public pages as ready)
    if (isPublicPage) {
      logger.log('[PluginInitializer] Skipping plugin registration for public page');
      return;
    }

    // Register plugins once on app startup
    registerAllPlugins()
      .then(() => {
        setPluginsRegistered(true);
        logger.log('[PluginInitializer] Plugins registered successfully');
      })
      .catch((err) => {
        logger.error('[PluginInitializer] Failed to register plugins:', err);
        setError(err.message);
        // Continue anyway - app should work without plugins
        setPluginsRegistered(true);
      });
  }, [isPublicPage]);

  // Show loading state (only for non-public pages)
  if (!pluginsRegistered && !isPublicPage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600 dark:border-blue-400"></div>
          <p className="text-gray-600 dark:text-gray-400">Initializing plugins...</p>
        </div>
      </div>
    );
  }

  // Show error if plugins failed to register (but continue)
  if (error) {
    logger.warn('[PluginInitializer] Continuing without plugins:', error);
  }

  return <>{children}</>;
}
